<template>
  <div role="dialog" class="popup fadein absolute h-full w-full z-50 flex justify-center items-center">
    <div class="popup-wrapper z-50 w-1/3 max-w-md flex flex-row-reverse rounded-xl bg-slate-middle">
      <div
        v-if="!props.join"
        tabindex="0"
        ref="firstFocus"
        class="absolute h-6 w-6 mr-2 mt-3 p-1.5 bg-black rounded-full flex justify-center items-center cursor-pointer"
        @click="close()"
      >
        <img class="w-5 h-5 invert rotate-45" :src="Close" :alt="t('aria-close')" />
      </div>
      <div class="flex flex-col justify-between w-full h-full px-9 py-9 font-thin bg-slate-middle rounded-xl mt-3">
        <p class="text-2xl font-bold text-slate-white">{{ t('headline') }}</p>
        <p class="text-lg my-2 text-slate-white text-center">{{ t('description') }}</p>
        <div class="flex flex-col py-5">
          <!-- If there is a permalink provided in the URL, we should only enter a username to establish a session -->
          <!-- If there is no permalink provided in the URL, we can try to join by PIN and username -->
          <div v-if="!state.permalink" class="flex items-center mt-3">
            <img class="opacity-80 w-9 p-1 invert" :src="Close" />
            <input
              v-model="state.pin"
              class="ml-3 text-slate-white text-lg w-full bg-slate-dark py-2 px-4 rounded-3xl without_number"
              type="number"
              placeholder="PIN"
            />
          </div>
          <div class="flex items-center bg-slate-middle mt-3">
            <img class="opacity-80 w-8 p-1 invert" :src="User" />
            <input
              v-model="state.name"
              class="ml-4 text-slate-white text-lg w-full bg-slate-dark py-2 px-4 rounded-3xl"
              type="text"
              :placeholder="t('name')"
            />
          </div>
        </div>
        <div class="pt-5 flex justify-end items-center w-full">
          <button class="blue-btn cursor-pointer p-3 px-5 text-md text-slate-white" @pointerup="join()">{{ t('join') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, onMounted, onBeforeMount, computed, nextTick, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { DialogMessageType, IWall } from '@/common/classes'
  import useDialogStore from '@/composables/dialog'

  import Close from '@/assets/icons/fontawesome/plus-solid.svg'
  import User from '@/assets/icons/fontawesome/user-solid.svg'
  import Link from '@/assets/icons/fontawesome/link-simple-regular.svg'
  import { IWallPayload } from '@/common/interfaces'
  import useCommonStore from '@/composables/common'
  import router from '@/routes'
  import { useRoute } from 'vue-router'
  import { useUserStore } from '@/composables/userStore'

  const messages = {
    no: {
      headline: 'Delta',
      description: 'Velg et kallenavn',
      join: 'Delta',
      name: 'navn',
      missingdetails: 'mangler opplysninger, vennligst oppgi alle detaljer',
    },
    en: {
      headline: 'Join',
      description: 'Choose a nickname',
      join: 'Join',
      name: 'name',
      missingdetails: 'missing details, please enter all details',
    },
  }

  const dialogStore = useDialogStore()
  const userStore = useUserStore()
  const common = useCommonStore()
  const route = useRoute()

  const emit = defineEmits<{
    (e: 'closePopup'): void
  }>()

  const { t } = useI18n({ messages })
  let isAuthenticated = false

  function close() {
    emit('closePopup')
  }

  const props = defineProps({
    join: { type: Boolean, default: false },
  })

  interface State {
    name: string
    pin: number | undefined
    permalink: string
  }

  const state = reactive<State>({
    name: '',
    pin: undefined,
    permalink: '',
  })

  const firstFocus = ref()
  onMounted(() => nextTick(() => firstFocus.value?.focus()))

  onBeforeMount(async () => {
    state.permalink = route.query.link?.toString() ?? ''
    const { authenticated } = await userStore.actions.isValidSession()
    isAuthenticated = authenticated
    if (state.permalink && authenticated) {
      // We are already logged in with FEIDE
      state.name = userStore.getters.user.value?.profile.username ?? ''
      join()
    }
  })

  const inputsAreValid = computed(() => {
    const pinIsValid = state.permalink || (state.pin && /^\d+$/.test(state.pin.toString()))
    const nameIsValid = /^[^(){}]+$/.test(state.name) || isAuthenticated
    return pinIsValid && nameIsValid
  })

  async function join() {
    if (inputsAreValid.value) {
      const payload: IWallPayload = {
        username: state.name,
        pin: state.pin,
        permalink: state.permalink,
      }
      const res = await userStore.actions.anonymousLogin(payload)
      if ((res.status && res.id) || isAuthenticated) {
        //Fix 1 to restore browser refresh
        //await userStore.actions.getUserDetails()
        let queryString = `/wall?id=${res.id}` // we want to proceed if a wall was selected
        router.push(queryString)
      }
    } else {
      dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('missingdetails'))
    }
  }
</script>

<style scoped>
  input[type='number'].without_number::-webkit-inner-spin-button,
  input[type='number'].without_number::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
</style>
